import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { NavLink, useLocation, useNavigate, type NavLinkProps } from 'react-router-dom';
import { ConfirmModal } from '@/shared/components/Modal';
import {
  DEFAULT_MESSAGE,
  guardContext,
  useGuardCtx,
  type GuardCtx,
  type GuardState,
} from './unsavedChangesGuardContext';

/**
 * App-wide "unsaved changes" guard. Wrap the app shell in {@link UnsavedChangesGuardProvider};
 * pages call {@code useUnsavedChangesGuard(dirty)} from {@code ./useUnsavedChangesGuard}; and
 * sidebar / shell links use {@link GuardedNavLink} so SPA nav respects the guard.
 *
 * <p>We can't use react-router v6's {@code useBlocker} because it's data-router only and we
 * mount the JSX {@code <Routes>} flavor ({@code main.tsx} renders {@code <BrowserRouter>}).
 * This provider gives the same UX without the full {@code createBrowserRouter} migration.
 *
 * <h3>Browser Back / Forward (BigList #14, slice-3557)</h3>
 * A {@code popstate} listener alone is useless: by the time it fires the browser has already
 * navigated, react-router has already swapped the route, and the dirty page has already
 * unmounted with its form state gone — cancelling the prompt could not put it back. So we
 * arm a <b>sentinel</b> instead: the moment a page registers as dirty we
 * {@code pushState} a byte-for-byte duplicate of the current entry (same URL, same
 * react-router {@code state} — so the same {@code idx}/{@code key}; react-router never sees
 * a location change and the page never re-mounts). The first Back press then pops the
 * sentinel and lands on the identical URL: nothing unmounts, the form is intact, and we can
 * show the normal confirm modal.
 * <ul>
 *   <li><b>Cancel</b> — we re-arm the sentinel, so the very next Back press is caught too.</li>
 *   <li><b>Discard</b> — {@code history.go(-2)} skips the re-armed sentinel plus the entry the
 *       user was on, i.e. the one real step back they asked for.</li>
 *   <li><b>Clean page</b> — a leftover sentinel (the page saved, so the guard went clean)
 *       swallows one Back press, so we forward it on with {@code history.go(-1)}.</li>
 * </ul>
 * Pushing the sentinel also drops any forward entries, which is what makes Forward safe while
 * dirty. {@code beforeunload} (in {@code useUnsavedChangesGuard}) still covers tab close /
 * refresh / typed-URL nav; browsers ignore our copy there and show their own, which is
 * unavoidable.
 *
 * <p>Known edges: (a) if the dirty page is the very first entry in the tab's history there is
 * nowhere to go back to, so the {@code go(-2)} is a no-op and the user simply stays put;
 * (b) a multi-step jump from the browser's history long-press menu skips over the sentinel,
 * so it lands where the user picked and the edits are lost — an intercept that needs a data
 * router; (c) a sentinel left behind by an earlier dirty page duplicates that page's entry,
 * which can cost one visually-inert Back press on the way past it; (d) if a dirty page pushes
 * its own history entries (a {@code setSearchParams} filter change, say), a Back press pops
 * onto the sentinel's URL before we prompt, so Cancel leaves the query string one step back
 * from where the operator pressed Back.
 */
export function UnsavedChangesGuardProvider({ children }: { children: ReactNode }) {
  // Registrants kept in a ref so registering / unregistering doesn't re-render every page.
  // Keyed per hook instance (see GuardCtx.setActive): a page and a component nested inside it
  // can both be registered, and the guard prompts while ANY of them is dirty.
  const registryRef = useRef<Map<object, GuardState>>(new Map());
  // href the currently-armed Back sentinel duplicates, or null when no sentinel is armed.
  const sentinelRef = useRef<string | null>(null);
  const [pending, setPending] = useState<{
    message: string;
    resolve: (ok: boolean) => void;
  } | null>(null);

  /** Duplicate the current history entry so the next Back press is a no-op we can intercept. */
  const armSentinel = useCallback(() => {
    if (typeof window === 'undefined') return;
    const here = window.location.href;
    if (sentinelRef.current === here) return; // already armed for this URL
    try {
      // Copy react-router's own state verbatim: same idx means its internal index bookkeeping
      // (and the delta it reports on pop) stays exactly as if we had never pushed.
      window.history.pushState(window.history.state, '', here);
      sentinelRef.current = here;
    } catch {
      // pushState can throw (Safari's 100-call limit). Degrade to beforeunload-only coverage.
      sentinelRef.current = null;
    }
  }, []);

  const setActive: GuardCtx['setActive'] = useCallback(
    (key, state) => {
      if (state == null) registryRef.current.delete(key);
      else registryRef.current.set(key, state);
      if (state?.dirty) armSentinel();
    },
    [armSentinel],
  );

  /** The first dirty registrant, whose message the prompt shows. */
  const firstDirty = useCallback((): GuardState | null => {
    for (const state of registryRef.current.values()) {
      if (state.dirty) return state;
    }
    return null;
  }, []);

  const ask = useCallback(() => {
    const a = firstDirty();
    if (!a) return Promise.resolve(true);
    return new Promise<boolean>((resolve) => {
      setPending({ message: a.message, resolve });
    });
  }, [firstDirty]);

  useEffect(() => {
    const onPop = () => {
      const armedFor = sentinelRef.current;
      // A pop we did not arm (or one that left a different URL) is a real Back/Forward we
      // cannot intercept after the fact — let react-router have it.
      if (!armedFor || armedFor !== window.location.href) {
        sentinelRef.current = null;
        return;
      }
      sentinelRef.current = null; // the sentinel entry is gone: it just got popped
      if (!firstDirty()) {
        // Clean page with a leftover sentinel: pass the user's Back press through.
        window.history.go(-1);
        return;
      }
      armSentinel(); // re-arm first, so a second Back press while the modal is open is caught
      void ask().then((ok) => {
        if (!ok) return; // Cancel: stay put, sentinel still armed
        // Drop every registration so the pop we are about to make isn't blocked again; the
        // pages being left re-register on their next mount.
        registryRef.current.clear();
        sentinelRef.current = null;
        window.history.go(-2); // the re-armed sentinel + the entry the user was on
      });
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [ask, armSentinel, firstDirty]);

  const value = useMemo<GuardCtx>(() => ({ setActive, ask }), [setActive, ask]);

  return (
    <guardContext.Provider value={value}>
      {children}
      <ConfirmModal
        open={!!pending}
        title="Discard unsaved changes?"
        message={pending?.message ?? DEFAULT_MESSAGE}
        confirmLabel="Discard"
        danger
        onConfirm={() => {
          pending?.resolve(true);
          setPending(null);
        }}
        onCancel={() => {
          pending?.resolve(false);
          setPending(null);
        }}
      />
    </guardContext.Provider>
  );
}

/**
 * Drop-in for {@code <NavLink>}. Intercepts the click, asks the guard, and navigates only
 * if the user confirmed (or the form is clean). Modifier-clicks (open in new tab/window)
 * pass through unchanged.
 *
 * <p>Slice-433 (FB#219 follow-up): NavLink's default isActive matches on
 * pathname only, so menu items that share a path but differ on query string
 * (e.g. {@code /labels/print-std?mode=license} vs
 * {@code /labels/print-std?mode=misc}) all light up together when ANY one
 * is selected. When the {@code to} prop contains a {@code ?...} query, this
 * wrapper installs a query-aware isActive that requires the link's search
 * params to be a subset of the current URL search params. Links without a
 * query keep React Router's default pathname-only behavior.
 */
/**
 * slice-3658 (Russ on demo via Chris 2026-09-15: "clicking new truck from the nav bar
 * highlights both new truck and truck search"): the query-aware rule above makes a
 * query-bearing link ({@code /browse/trucks?new=1}) light only on its own URL, but the BARE
 * sibling ({@code /browse/trucks}) still matched on pathname alone. {@code yieldToQueries} lists
 * the query-bearing targets that share this bare link's path; when the current URL satisfies
 * one of them, the bare link stands down. The sidebar computes the list from the nav tree.
 */
export type GuardedNavLinkProps = NavLinkProps & { yieldToQueries?: readonly string[] | undefined };

function queryMatchesHere(target: string, herePath: string, hereSearch: string): boolean {
  const qi = target.indexOf('?');
  if (qi < 0) return false;
  if (target.slice(0, qi) !== herePath) return false;
  const want = new URLSearchParams(target.slice(qi + 1));
  const here = new URLSearchParams(hereSearch);
  let all = true;
  want.forEach((v, k) => {
    if (here.get(k) !== v) all = false;
  });
  return all;
}

export function GuardedNavLink(props: GuardedNavLinkProps) {
  const { ask } = useGuardCtx();
  const navigate = useNavigate();
  const location = useLocation();
  const { to, onClick, className, yieldToQueries, ...rest } = props;
  const toStr = typeof to === 'string' ? to : (to.pathname ?? '') + (to.search ?? '');
  const queryIdx = toStr.indexOf('?');
  const hasQuery = queryIdx >= 0;
  const yields = !hasQuery && (yieldToQueries?.length ?? 0) > 0;
  // Build the className prop only when set, to satisfy exactOptionalPropertyTypes.
  const queryAwareClassName = yields
    ? (props2: { isActive: boolean; isPending: boolean; isTransitioning: boolean }) => {
        // Bare link: stand down when a query-bearing sibling on the same path is the match.
        const taken = (yieldToQueries ?? []).some((q) =>
          queryMatchesHere(q, location.pathname, location.search),
        );
        const isActive = props2.isActive && !taken;
        if (typeof className === 'function') return className({ ...props2, isActive }) ?? '';
        return className ?? '';
      }
    : hasQuery
    ? (props2: { isActive: boolean; isPending: boolean; isTransitioning: boolean }) => {
        const toPath = toStr.slice(0, queryIdx);
        const toQuery = new URLSearchParams(toStr.slice(queryIdx + 1));
        const here = new URLSearchParams(location.search);
        const samePath = location.pathname === toPath;
        let allMatch = true;
        toQuery.forEach((v, k) => {
          if (here.get(k) !== v) allMatch = false;
        });
        const isActive = samePath && allMatch;
        if (typeof className === 'function') {
          const res = className({ ...props2, isActive });
          return res ?? '';
        }
        return className ?? '';
      }
    : null;
  return (
    <NavLink
      to={to}
      {...rest}
      {...(queryAwareClassName ? { className: queryAwareClassName } : className != null ? { className } : {})}
      onClick={(e) => {
        if (e.defaultPrevented) return;
        onClick?.(e);
        if (e.defaultPrevented) return;
        // Modifier-clicks open in new tab/window — let the browser handle them.
        if (e.ctrlKey || e.metaKey || e.shiftKey || e.altKey || e.button !== 0) return;
        e.preventDefault();
        void ask().then((ok) => {
          if (ok) navigate(typeof to === 'string' ? to : to.pathname ?? '/');
        });
      }}
    />
  );
}
